import { getStorage, setStorage } from "@helpers/storage";
import { defineStore } from "pinia";
import { watch } from "vue";
import { useAppStore } from "./appStore";
import { useUserStore } from "./userStore";
import { Api, DisplayApi } from "@helpers/api";
import { router } from "@router/router";

export const useDisplayStore = defineStore("display", {
    actions: {
        load() {
            // Sets up the display store on app load

            // Load data from localstorage
            this.uuid = getStorage("display_uuid");
            this.secret = getStorage("display_secret");
            this.verified_at = getStorage("display_verified_at");
            this.token_expires_at = getStorage("display_token_expires_at");

            // Setup userstore
            this.stores.appStore = useAppStore();
            this.stores.userStore = useUserStore();

            // Check if display is setup, if so redirect to display view
            if (this.uuid && this.verified_at) {
                router.push({ name: "/display" });
            }

            this.loaded = true;
        },
        init() {
            // Sets up the display store when the user navigates to the display view
            if (!this.setup) {
                this.load();
            }

            this.setup = true;

            // Update display status
            this.setStatus("connected_to_yhub", "trying");

            // Keep the websocket status updated when the connection changes.
            watch(
                () => this.stores.appStore.connections?.displayYhub,
                (connection) => {
                    this.setStatus(
                        "websocket_yhub",
                        !connection
                            ? "unknown"
                            : connection.enabled
                              ? connection.status
                              : "disabled",
                    );
                },
                { deep: true, immediate: true },
            );

            // Setup 1 min polling interval for chores (check status, check token ect.)
            this.pollInterval = setInterval(
                () => {
                    this.scheduler();
                },
                1 * 60 * 1000,
            );

            // No uuid found, send initiation request to the server
            if (!this.uuid) {
                return this.sendInitationRequest();
            }

            // Check status of the display
            return this.checkStatus();
        },
        setValue(key, value) {
            this[key] = value;
            setStorage("display_" + key, value);
        },
        sendInitationRequest() {
            Api.get("/connect/display/initiate-pairing").then((response) => {
                this.setValue("uuid", response.data.data.uuid);
                this.setValue("secret", response.data.data.secret_code);
                this.setStatus("connected_to_yhub", "success");
            });
        },
        authorize() {
            Api.post("/connect/display/authenticate", {
                display_id: this.uuid,
                display_secret: this.secret,
            })
                .then((response) => {
                    if (response.data.success) {
                        // Store token in userStore
                        this.stores.userStore.setToken("display", response.data.data.token);

                        // Store token expiration in display store: ToDo: move to user/tokenStore?
                        this.setValue("token_expires_at", this.display_token_expires_at);
                        this.setStatus("display_authorized", "success");

                        // Fetch config after authorization
                        this.fetchConfig();

                        // Start display WS connection
                        this.stores.appStore.enableConnection("displayYhub");
                    }
                })
                .catch((error) => {
                    this.setStatus("display_authorized", "error");
                });
        },
        checkStatus() {
            console.log("Checking display status...");
            Api.get("/connect/display/status/" + this.uuid, this.running).then((response) => {
                const data = response.data.data;

                // Update display store with data from the server
                this.setValue("uuid", data.uuid);
                this.setValue("name", data.name);
                this.setValue("verified_at", data.verified_at);

                // Update display status
                this.config_name = response.data.data.config_name;
                this.verified_at = response.data.data.verified_at;
                this.pairing = response.data.data.pairing;

                this.setStatus("connected_to_yhub", "success");

                if (!this.display_token_expires_at) {
                    this.authorize();
                }

                if (this.display_token_expires_at) {
                    this.fetchConfig();
                }

                if (this.isReady) {
                    router.push({ name: this.config.view.route });
                    this.running = true;
                }
            });
            // .catch((error) => {
            //     this.setStatus("connected_to_yhub", "error");
            // })
        },
        checkIfSetup() {
            if (!this.setup) {
                router.push({ name: "/display" });
            }
        },
        fetchConfig() {
            this.setStatus("display_configured", "trying");
            DisplayApi.get("/config", this.running)
                .then((response) => {
                    if (response.data.success) {
                        this.config = response.data.data;
                        this.setStatus("display_configured", "success");

                        this.setStatus(
                            "connected_to_labhub",
                            this.config.labhub_connection ? "pending" : "disabled",
                        );

                        if (this.config.labhub_connection) {
                            this.stores.appStore.enableConnection("displayLabhub");
                        }

                        if (this.isReady) {
                            router.push({ name: this.config.view.route });
                            this.running = true;
                        }
                    }
                })
                .catch((error) => {
                    this.setStatus("display_configured", "error");
                });
        },
        scheduler() {
            console.log("Running scheduler for display chores");

            // If the display is registered, check if current minutes % 5 is = 0, if so, check status
            const now = new Date();
            const minutes = now.getMinutes();
            if (this.verified_at && this.running && minutes % 5 !== 0) {
                // Skip status check if not on a 5 minute interval
                console.log("Skipping status check, not on a 5 minute interval (" + minutes + ")");
                return;
            }

            // Check if the display is still authorized and configured
            this.checkStatus();

            // Check if the display token is still valid
            if (this.display_token_expires_at) {
                // const expiresAt = new Date(this.display_token_expires_at);
                // const now = new Date();
                // if (now > expiresAt) {
                //     this.authorize();
                // }
            }
        },
        setStatus(key, value) {
            this.status_store[key] = value;
        },
        logoutCurrentUser() {
            this.stores.userStore.revokeToken("yhub");
            router.push({ name: "/display/view/login" });
        },
    },
    getters: {
        displayRegisterUrl() {
            const uuid = getStorage("uuid");

            if (uuid) {
                return "/connect/display/initiate-pairing?uuid=" + uuid;
            }
            return "/connect/display/initiate-pairing";
        },
        status() {
            const map_ws_sockets = {
                disconnected: "error",
                connecting: "trying",
                connected: "success",
            };

            return {
                connected_to_yhub: this.status_store.connected_to_yhub,
                display_authorized: this.status_store.display_authorized,
                display_configured: this.status_store.display_configured,
                websocket_yhub: this.stores.appStore?.connections
                    ? this.stores.appStore.connections.displayYhub.enabled
                        ? map_ws_sockets[this.stores.appStore.connections.displayYhub.status]
                        : "disabled"
                    : "unknown",
                connected_to_labhub: this.stores.appStore?.connections
                    ? this.stores.appStore.connections.displayLabhub.enabled
                        ? map_ws_sockets[this.stores.appStore.connections.displayLabhub.status]
                        : "disabled"
                    : "unknown",
            };
        },
        isReady() {
            return (
                this.status.connected_to_labhub === "success" &&
                this.status.connected_to_yhub === "success" &&
                this.status.display_authorized === "success" &&
                this.status.display_configured === "success" &&
                this.status.websocket_yhub === "success"
            );
        },
    },
    state: () => ({
        name: null,
        uuid: null,
        pairing: null,
        verified_at: null,
        secret: null,
        config_name: null,
        config: {},
        display_configured: "pending",
        status_store: {
            connected_to_yhub: "pending",
            display_authorized: "pending",
            display_configured: "pending",
        },
        stores: {},

        display_token_expires_at: null,
        pollInterval: null,
        setup: false,
        running: false,
        loaded: false,
    }),
});
